import { Injectable } from "@angular/core";
import { ModalController, PopoverController } from "@ionic/angular";
import { firstValueFrom } from "rxjs";
import { PerformanceObjectivesPopoverComponent } from "src/app/pages/modulo-desempeno/components/performance-objectives-popover/performance-objectives-popover.component";
import { ProcessSummary } from "./proceso-desempeno.model";
import { ProcesoDesempenoService } from "./proceso-desempeno.service";

/** Cómo se presenta el selector: popover anclado (desktop) o sheet modal (móvil). */
export type ProcessSelectionPresentation = "popover" | "sheet";

/**
 * Abre el selector de proceso del ítem "Mis objetivos" del menú de Desempeño.
 *
 * Es el único dueño de esa apertura —antes estaba duplicada en
 * {@link MyHeaderComponent} y {@link FooterTabComponent}— porque la decisión de
 * **si conviene abrirlo** necesita la lista de procesos, que ninguno de los dos
 * conocía: la cargaba el propio popover al montarse.
 *
 * Con **un solo proceso** no hay nada que elegir, así que se entra directo y el
 * overlay ni siquiera se presenta (mismo criterio que la etapa de Calibración con
 * un solo evaluado). Se resuelve **antes** de abrir a propósito: abrirlo y
 * cerrarlo solo se vería, en móvil, como el sheet subiendo y bajando.
 */
@Injectable({
	providedIn: "root",
})
export class PerformanceProcessSelectionService {
	/** Apertura en curso: evita que un doble toque abra dos overlays. */
	private opening = false;

	constructor(
		private procesoService: ProcesoDesempenoService,
		private popoverController: PopoverController,
		private modalController: ModalController,
	) {}

	/**
	 * Resuelve la lista de procesos y decide: con uno solo entra directo a su
	 * detalle, con cero o varios presenta el selector con la lista ya cargada (así
	 * el componente no vuelve a pedirla ni muestra el spinner).
	 */
	async open(options: { event?: Event; presentation: ProcessSelectionPresentation }): Promise<void> {
		if (this.opening) return;
		this.opening = true;

		try {
			const processes = await this.loadProcesses();

			if (processes?.length === 1) {
				this.procesoService.enterProcess(processes[0]);
				return;
			}

			// Cero procesos también abre: el componente muestra su estado vacío
			// ("No tienes procesos disponibles").
			await this.present(processes, options);
		} finally {
			this.opening = false;
		}
	}

	/**
	 * Lista de procesos, o `null` si la lectura falló. Un fallo no deja el toque sin
	 * respuesta: se abre el selector sin precargar y el componente reintenta por su
	 * cuenta, como hacía antes de centralizar la apertura.
	 */
	private async loadProcesses(): Promise<ProcessSummary[] | null> {
		try {
			return await firstValueFrom(this.procesoService.getProcessSummaries());
		} catch {
			return null;
		}
	}

	private async present(
		processes: ProcessSummary[] | null,
		options: { event?: Event; presentation: ProcessSelectionPresentation },
	): Promise<void> {
		if (options.presentation === "sheet") {
			// El footer vive pegado al borde inferior de la pantalla, así que un popover
			// se recorta (no hay espacio debajo del tab). Se usa un sheet modal que sube
			// desde la barra inferior, con scroll interno y sin recortes.
			const modal = await this.modalController.create({
				component: PerformanceObjectivesPopoverComponent,
				componentProps: { preloadedProcesses: processes },
				breakpoints: [0, 0.6, 0.9],
				initialBreakpoint: 0.6,
				handle: true,
				cssClass: "performance-objectives-sheet",
			});
			await modal.present();
			return;
		}

		const popover = await this.popoverController.create({
			component: PerformanceObjectivesPopoverComponent,
			componentProps: { preloadedProcesses: processes },
			event: options.event,
			cssClass: "performance-objectives-popover",
			showBackdrop: true,
		});
		await popover.present();
	}
}
